import config from "../config";
import { decodeJwtPayload } from "./token";

// The admin cannot read the user site's cookies, so it embeds a page served from
// that origin which reads them and posts the token back. That page is a static
// file (nextdesktop repo, public/next-desktop/silent-auth.html) because dev and
// staging put HTTP Basic auth in front of every path but /public/*, and browsers
// suppress that prompt inside a cross-origin iframe.
export const MESSAGE_TYPE = "cfj-silent-auth";
export const SILENT_AUTH_PATH = "/public/next-desktop/silent-auth.html";
export const DEFAULT_TIMEOUT_MS = 8000;

const ATTEMPTED_KEY = "cfj.silent-auth.attempted";
const BOUNCED_KEY = "cfj.silent-auth.bounced";
const ATTEMPTS_KEY = "cfj.silent-auth.attempts";

const session = () => {
  try {
    return window.sessionStorage;
  } catch (e) {
    return null;
  }
};

const readAttempt = () => {
  const store = session();
  const raw = store && store.getItem(ATTEMPTED_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (e) {
    return null;
  }
};

// The account a token belongs to, from whichever claim the issuer uses.
export const tokenSubject = claims => (claims && (claims.sub || claims._id || claims.id || claims.userId)) || null;

// Keyed on the account rather than on the token string: the user site mints a
// fresh JWT on every handshake (silent-auth.html answers with renewToken's
// output), so comparing tokens would call every loop a new sign-in. The same
// account coming back after a refusal is the loop; a different one is a real
// sign-in worth adopting.
export const markAttempted = (token, claims) => {
  const store = session();
  if (store) store.setItem(ATTEMPTED_KEY, JSON.stringify({ token, sub: tokenSubject(claims), roles: (claims && claims.roles) || [] }));
};

export const hasAttempted = () => !!readAttempt();

export const attemptedToken = () => {
  const attempt = readAttempt();
  return (attempt && attempt.token) || null;
};

export const attemptedSubject = () => {
  const attempt = readAttempt();
  return (attempt && attempt.sub) || null;
};

export const attemptedRoles = () => {
  const attempt = readAttempt();
  return attempt && Array.isArray(attempt.roles) ? attempt.roles : null;
};

export const clearAttempted = () => {
  const store = session();
  if (store) store.removeItem(ATTEMPTED_KEY);
};

// Counted rather than allowed once: if the user site ever sends someone straight
// back still signed out, the two would bounce forever with no screen to read.
export const MAX_LOGIN_BOUNCES = 2;

export const loginBounces = () => {
  const store = session();
  return Number((store && store.getItem(BOUNCED_KEY)) || 0);
};

export const recordLoginBounce = () => {
  const store = session();
  if (store) store.setItem(BOUNCED_KEY, String(loginBounces() + 1));
};

export const clearLoginBounces = () => {
  const store = session();
  if (store) store.removeItem(BOUNCED_KEY);
};

// Last line of defence, below every other guard: the markers above each cover a
// known way to loop (the same account refused again, user site sending a
// signed-out visitor straight back), so a loop reaching this one is a way nobody
// predicted — a redirect coming from somewhere else in the app, an API that
// accepts the token for `me` and refuses it everywhere else. It counts page
// loads rather than reasons, and nothing but an explicit sign-out or "Try again"
// may clear it: a loop that renders an authenticated page each time round used
// to reset this and spin forever.
export const MAX_AUTH_ATTEMPTS = 5;

// Attempts age out instead, so a count left behind by a loop that has since been
// resolved cannot lock a later sign-in out of a tab that stays open for days.
// Five sign-in page loads inside a minute is a loop; spread over longer it is a
// person.
export const ATTEMPT_WINDOW_MS = 60 * 1000;

const readAttempts = () => {
  const store = session();
  const raw = store && store.getItem(ATTEMPTS_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const cutoff = Date.now() - ATTEMPT_WINDOW_MS;
    return parsed.filter(at => typeof at === "number" && at > cutoff);
  } catch (e) {
    return [];
  }
};

export const authAttempts = () => readAttempts().length;

export const recordAuthAttempt = () => {
  const next = [...readAttempts(), Date.now()];
  const store = session();
  if (store) store.setItem(ATTEMPTS_KEY, JSON.stringify(next));
  return next.length;
};

export const clearAuthAttempts = () => {
  const store = session();
  if (store) store.removeItem(ATTEMPTS_KEY);
};

export const userSiteOrigin = () => process.env.REACT_APP_USER_SITE_ORIGIN || config.frontURI;

export const silentAuthUrl = nonce => {
  const url = new URL(SILENT_AUTH_PATH, userSiteOrigin());
  url.searchParams.set("nonce", nonce);
  return url.toString();
};

export const userSiteSsoUrl = token => {
  const url = new URL("/sso", userSiteOrigin());
  url.searchParams.set("token", token);
  return url.toString();
};

export const DEFAULT_REDIRECT = "/dashboard";

// `redirect` is whatever the query string says, so it is attacker-controllable via
// a crafted link. Only a path on this origin may be followed: `window.location`
// treats "//evil.com" and "/\\evil.com" as absolute URLs, not as paths. /login
// itself is refused too — it is where the handshake already is, and sending it
// back there would spin.
export const safeRedirect = (value, fallback = DEFAULT_REDIRECT) => {
  if (typeof value !== "string") return fallback;
  if (!value.startsWith("/")) return fallback;
  if (value.startsWith("//") || value.startsWith("/\\")) return fallback;
  if (value === "/login" || value.startsWith("/login?") || value.startsWith("/login#")) return fallback;
  return value;
};

export const userSiteLoginUrl = (returnTo = window.location.href) => {
  const url = new URL("/login", userSiteOrigin());
  if (returnTo) url.searchParams.set("return_to", returnTo);
  return url.toString();
};

const makeNonce = () => {
  const buf = new Uint8Array(16);
  window.crypto.getRandomValues(buf);
  return Array.from(buf)
    .map(b => b.toString(16).padStart(2, "0"))
    .join("");
};

// Always resolves, never rejects:
// { status: "ok", accessToken } | { status: "no-session" | "error" | "timeout" }
export const runSilentAuth = ({ timeoutMs = DEFAULT_TIMEOUT_MS, onTrace } = {}) =>
  new Promise(resolve => {
    const nonce = makeNonce();
    const src = silentAuthUrl(nonce);
    const expectedOrigin = new URL(src).origin;
    const trace = (text, level) => onTrace && onTrace(text, level || "info");

    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.setAttribute("title", "silent auth");
    iframe.style.cssText = "position:absolute;width:0;height:0;border:0;visibility:hidden";

    let settled = false;
    let timer = null;

    const finish = result => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      window.removeEventListener("message", onMessage);
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      resolve(result);
    };

    // Any frame or extension on the page can post here, so all four checks must
    // pass. A failing one is ignored, not fatal: an unrelated message must not
    // end the handshake.
    function onMessage(event) {
      if (event.origin !== expectedOrigin) return;
      if (event.source !== iframe.contentWindow) return trace("ignored message from an unexpected frame", "warn");

      const data = event.data || {};
      if (data.type !== MESSAGE_TYPE) return;
      if (data.nonce !== nonce) return trace("ignored message with a stale nonce", "warn");

      if (data.status === "ok" && data.accessToken) {
        trace("received a token from the user site", "ok");
        return finish({ status: "ok", accessToken: data.accessToken });
      }
      trace(`user site reported status="${data.status}"`, "warn");
      finish({ status: data.status === "no-session" ? "no-session" : "error" });
    }

    window.addEventListener("message", onMessage);
    trace(`asking ${expectedOrigin} for a token`);

    timer = setTimeout(() => {
      trace(`no answer after ${timeoutMs}ms`, "error");
      finish({ status: "timeout" });
    }, timeoutMs);

    iframe.src = src;
    document.body.appendChild(iframe);
  });

export const IMPERSONATION_SYNC_TIMEOUT_MS = 15000;
export const IMPERSONATION_POLL_INTERVAL_MS = 750;

export const waitForUserSiteToken = async (userId, { timeoutMs = IMPERSONATION_SYNC_TIMEOUT_MS } = {}) => {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const remaining = deadline - Date.now();
    const result = await runSilentAuth({ timeoutMs: Math.min(DEFAULT_TIMEOUT_MS, remaining) });
    if (result.status === "ok" && tokenSubject(decodeJwtPayload(result.accessToken)) === userId) {
      return result.accessToken;
    }
    if (Date.now() + IMPERSONATION_POLL_INTERVAL_MS >= deadline) break;
    await new Promise(resolve => setTimeout(resolve, IMPERSONATION_POLL_INTERVAL_MS));
  }
  return null;
};
